import Promise from 'bluebird';
import { keyBy, zipObject } from 'lodash';
import moment from 'moment';
import { asciiToHex } from 'web3-utils';
import fetchContractEvents from '@blockcatio/tabby-library/lib/utilities/fetchContractEvents';
import safeToBn from '@blockcatio/tabby-library/lib/utilities/safeToBn';

import eventToConfirmedCampaign from '../utilities/eventToConfirmedCampaign';
import getRewardStatus from '../utilities/getRewardStatus';
import getCampaignStatus from '../utilities/getCampaignStatus';
import { CampaignStatuses, RewardStatuses } from '../constants';
import { deriveVrs } from '../utilities/publicKeyCrypto';
import getRewardKey from '../utilities/getRewardKey';

export const getInfoFromSignature = deriveVrs;

const CAMPAIGN_CREATED_EVENT = 'CampaignCreated';
const REWARD_REDEEMED_EVENT = 'RewardRedeemed';
const REWARD_CANCELLED_EVENT = 'RewardCancelled';
const CAMPAIGN_CANCELLED_EVENT = 'CampaignCancelled';

class TabbyRewardsContract {
  constructor(config, eth) {
    this.id = config.id;
    this.eth = eth;
    this.config = {
      initialBlockNumber: 0,
      ...config,
    };

    this.contract = new eth.web3Eth.Contract(config.abi);
    this.contract.options.address = config.address;
  }

  /**
   * Fetches the ETH fee set by the contract
   *
   * @return {Promise<BN>}
   */
  async fetchEthFee() {
    return this.contract.methods
      .ethFee()
      .call()
      .then(fee => safeToBn(fee));
  }

  /**
   * Fetches the CAT fee set by the contract
   *
   * @return {Promise<BN>}
   */
  async fetchCatFee() {
    return this.contract.methods
      .catFee()
      .call()
      .then(fee => safeToBn(fee));
  }

  /**
   * Create a new campaign and pay for it.
   *
   * @param {Object} campaignDetails
   * @param {BN|wei/tokenWei} paymentFee
   * @param {Bool} useCat
   * @param {String|wei} gasPrice
   */
  createCampaignTransaction(campaignDetails, paymentFee, useCat, gasPrice = '2000000000') {
    return {
      transaction: this.contract.methods.createCampaign(
        campaignDetails.name || 'No Name Set Yet',
        asciiToHex(campaignDetails.id),
        campaignDetails.signer,
        campaignDetails.tokenAddress,
        // tokensPerCode is in wei.
        campaignDetails.tokensPerCode,
        campaignDetails.numberOfCodes,
        useCat,
      ),
      contract: this.config.address,
      sendOptions: {
        from: campaignDetails.sender,
        value: !useCat ? paymentFee : 0,
        gasPrice,
        gas: useCat
          ? this.config.createCampaignByCatGasLimit
          : this.config.createCampaignByEthGasLimit,
      },
    };
  }

  /**
   * Creates the transaction for cancelling a campaign
   *
   * @param {Object} Campaign
   * @param {String} fromAddress
   * @param {String} gasPrice
   * @param {Number} gasLimit
   * @return {Object} a transaction object to be used by EthGateway
   */
  cancelCampaignTransaction(
    { id },
    fromAddress,
    gasPrice,
    gasLimit = this.config.cancelCampaignGasLimit,
  ) {
    return {
      transaction: this.contract.methods.cancelCampaign(asciiToHex(id)),
      contract: this.config.address,
      sendOptions: {
        from: fromAddress,
        gasPrice,
        gas: gasLimit,
      },
    };
  }

  /**
   * Creates the transaction for cancelling a reward
   *
   * @param {Object} Campaign
   * @param {Number} rewardNumber
   * @param {String} fromAddress
   * @param {String} gasPrice
   * @param {Number} gasLimit
   * @return {Object} a transaction object to be used by EthGateway
   */
  cancelRewardTransaction(
    { id },
    rewardNumber,
    fromAddress,
    gasPrice,
    gasLimit = this.config.redeemRewardGasLimit,
  ) {
    return {
      transaction: this.contract.methods.cancelCode(asciiToHex(id), rewardNumber),
      contract: this.config.address,
      sendOptions: {
        from: fromAddress,
        gasPrice,
        gas: gasLimit,
      },
    };
  }

  /**
   * Creates the transaction for redeeming a reward code
   *
   * @param {Object} Campaign
   * @param {Number} rewardNumber
   * @param {String} code
   * @param {String} fromAddress
   * @param {String} gasPrice
   * @param {Number} gasLimit
   * @return {Object} a transaction object to be used by EthGateway
   */
  redeemRewardTransaction(
    { id },
    rewardNumber,
    code,
    fromAddress,
    gasPrice = '2000000000',
    gasLimit = this.config.cancelRewardGasLimit,
  ) {
    // Extract the variables needed to verify signer with ecrecover
    // https://solidity.readthedocs.io/en/latest/units-and-global-variables.html#mathematical-and-cryptographic-functions
    const { r, s, v } = getInfoFromSignature(code);
    return {
      transaction: this.contract.methods.redeemCode(
        asciiToHex(id),
        rewardNumber,
        v,
        r,
        s,
        fromAddress,
      ),
      contract: this.config.address,
      sendOptions: {
        from: fromAddress,
        gasPrice,
        gas: gasLimit,
      },
    };
  }

  fetchCampaign(campaignId) {
    return this.contract.methods.campaigns(asciiToHex(campaignId)).call();
  }

  getHash(campaignId, i) {
    return this.contract.methods.getHash(asciiToHex(campaignId), i).call();
  }

  getSigner(message, signature) {
    // Extract the variables needed to verify signer with ecrecover.
    // https://solidity.readthedocs.io/en/latest/units-and-global-variables.html#mathematical-and-cryptographic-functions
    const { r, s, v } = getInfoFromSignature(signature);
    return this.contract.methods.getSigner(message, v, r, s).call();
  }

  /**
   * Fetches all campaigns for a given address, searching events up to latest block number.
   *
   * @param {String} address
   * @param {Number} latestBlockNumber
   * @return {Promise<Object[]>} the campaigns
   */
  fetchCampaigns(address, latestBlockNumber) {
    return this.fetchCampaignEvents(address, latestBlockNumber, eventToConfirmedCampaign);
  }

  /**
   * Fetch campaign events and transform them into campaign objects.
   *
   * @param {String} address
   * @param {Number} latestBlockNumber
   * @param {Function} eventFormatter
   *
   * @returns {Promise<Object[]>} the campaigns
   */
  async fetchCampaignEvents(address, latestBlockNumber, eventFormatter) {
    // fetch create events first (build list of ids, blocks)
    const createEvents = await fetchContractEvents(
      this.contract,
      {
        eventName: CAMPAIGN_CREATED_EVENT,
        fromBlock: this.config.initialBlockNumber,
        toBlock: latestBlockNumber,
        filter: {
          sender: address,
        },
      },
      25000,
    );

    // Fetch more info/history if we found events.
    if (createEvents.length) {
      // fetch blocks and events against those ids in parallel
      const fetchBlocks = this.eth.fetchBlocks(createEvents.map(event => event.blockNumber));
      // Fetching by event in separate fetches ends up being more efficient than fetching all,
      // and filtering later.
      const eventFetches = [
        REWARD_REDEEMED_EVENT,
        REWARD_CANCELLED_EVENT,
        CAMPAIGN_CANCELLED_EVENT,
      ].map(eventName =>
        fetchContractEvents(
          this.contract,
          {
            eventName,
            fromBlock: this.config.initialBlockNumber,
            toBlock: latestBlockNumber,
            filter: {
              campaignId: createEvents.map(event => event.returnValues.campaignId),
            },
          },
          25000,
        ));
      const fetchRestOfEvents = Promise.reduce(
        eventFetches,
        (allEvents, events) => (events ? [...allEvents, ...events] : [...allEvents]),
      );

      // Fetch our supporting data.
      const [blocks, restOfEvents] = await Promise.all([fetchBlocks, fetchRestOfEvents]);

      let campaignEvents = {};
      if (restOfEvents) {
        campaignEvents = restOfEvents.reduce(
          (events, item) => ({
            ...events,
            [item.returnValues.campaignId]: events[item.returnValues.campaignId].concat(item),
          }),
          zipObject(
            restOfEvents.map(event => event.returnValues.campaignId),
            restOfEvents.map(() => []),
          ),
        );
      }

      const blocksByNumber = keyBy(blocks, 'blockNumber');

      // convert to our Campaign shape, then apply further events to update rewards or status.
      return createEvents
        .map(event => ({ ...event, contract: this.id }))
        .map(event => eventFormatter(event, blocksByNumber[event.blockNumber].timestamp))
        .map((campaign) => {
          if (campaignEvents[campaign.hexId]) {
            return campaignEvents[campaign.hexId].reduce((fullCampaign, nextEvent) => {
              if (nextEvent.event === REWARD_CANCELLED_EVENT) {
                return {
                  ...fullCampaign,
                  blockNumber: nextEvent.blockNumber,
                  rewards: [
                    ...fullCampaign.rewards,
                    {
                      rewardNumber: parseInt(nextEvent.returnValues.i, 10),
                      rewardKey: getRewardKey(campaign.id, nextEvent.returnValues.i),
                      status: getRewardStatus(RewardStatuses.cancelled),
                    },
                  ],
                };
              }
              if (nextEvent.event === REWARD_REDEEMED_EVENT) {
                return {
                  ...fullCampaign,
                  blockNumber: nextEvent.blockNumber,
                  rewards: [
                    ...fullCampaign.rewards,
                    {
                      rewardNumber: parseInt(nextEvent.returnValues.i, 10),
                      rewardKey: getRewardKey(campaign.id, nextEvent.returnValues.i),
                      status: getRewardStatus(RewardStatuses.redeemed),
                    },
                  ],
                };
              }
              if (nextEvent.event === CAMPAIGN_CANCELLED_EVENT) {
                return {
                  ...fullCampaign,
                  status: getCampaignStatus(CampaignStatuses.cancelled),
                };
              }
              return fullCampaign;
            }, campaign);
          }
          return campaign;
        })
        .reduce((keyed, campaign) => ({ ...keyed, [campaign.id]: campaign }), {});
    }
    return [];
  }

  /**
   * Fetches events since a given block.
   *
   * @param {Number} fromBlock
   * @return {Promise<Array{}>} - events
   */
  async fetchEventsSince(fromBlock) {
    return fetchContractEvents(this.contract, {
      fromBlock,
      toBlock: 'latest',
    })
      .filter(event =>
        event.event &&
          (event.event.indexOf('Campaign') === 0 || event.event.indexOf('Reward') === 0))
      .map(event => ({
        ...event,
        timestamp: moment().unix(),
        contract: this.id,
      }));
  }

  /**
   * Fetches information about a reward
   *
   * @param {String} campaignId
   * @param {Number} rewardNumber - reward number within campaign
   * @return {Promise<Object>}
   */
  async fetchReward(campaignId, rewardNumber) {
    const [campaign, reward] = await Promise.all([
      this.fetchCampaign(campaignId),
      this.contract.methods.getCodeStatus(asciiToHex(campaignId), rewardNumber).call(),
    ]);

    if (campaign && reward) {
      return {
        redeemable: reward['0'],
        value: safeToBn(reward['1']),
        campaign: {
          ...campaign,
          id: campaignId,
          contract: this.id,
        },
      };
    }
    return {};
  }
}

export default TabbyRewardsContract;



// WEBPACK FOOTER //
// ./src/services/TabbyRewardsContract.js